import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ExpandMore, Visibility, Replay, Circle } from '@mui/icons-material';
import { FlattenedResults } from '../../hooks/useAIObjects';

interface ObjectDetectionButtonProps {
  isPendingGeneration: () => boolean;
  progress: { done: number; total: number };
  objectDetectionResults: FlattenedResults;
  onViewObjects: () => void;
  onReanalyze: () => void;
  isSmallDisplay?: boolean;
}

export const ObjectDetectionButton: React.FC<ObjectDetectionButtonProps> = ({
  isPendingGeneration,
  objectDetectionResults,
  onViewObjects,
  onReanalyze,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const isProcessing = isPendingGeneration();
  const objectCount = Object.keys(objectDetectionResults).length;
  const hasResults = objectCount > 0;

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleViewObjects = useCallback(() => {
    handleMenuClose();
    onViewObjects();
  }, [onViewObjects, handleMenuClose]);

  const handleReanalyzeFromMenu = useCallback(() => {
    handleMenuClose();
    onReanalyze();
  }, [onReanalyze, handleMenuClose]);

  const renderMainButton = () => {
    let startIcon = (
      <Circle sx={{ height: '6px', width: '6px' }} color="warning" />
    );
    let endIcon = undefined as any;
    let text = 'No Objects Detected';
    let badge = undefined as any;
    if (isProcessing) {
      startIcon = <CircularProgress size={20} sx={{ color: 'grey.500' }} />;
      text = 'Analyzing...';
    } else if (hasResults) {
      startIcon = (
        <Circle sx={{ height: '6px', width: '6px', m: 0 }} color="info" />
      );
      endIcon = <ExpandMore />;
      text = 'Objects Detected';
      badge = (
        <Box
          component="span"
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            bgcolor: 'info.light',
            borderRadius: '2px',
            p: '2px 5px',
          }}
        >
          {objectCount}
        </Box>
      );
    }

    return (
      <Button
        variant="outlined"
        startIcon={startIcon}
        endIcon={endIcon}
        onClick={handleMenuOpen}
        sx={{
          display: 'flex',
          gap: 1,
          my: 1,
          p: 1,
          maxHeight: '32px',
          fontWeight: 500,
          fontSize: { sm: '12px', md: '14px' },
          fontStyle: 'normal',
          lineHeight: '16px',
          textTransform: 'capitalize',
          color: 'text.secondary',
          borderColor: 'text.secondary',
          borderRadius: '8px',
          minWidth: '215px',
          '&:hover': {
            borderColor: 'grey.600',
            color: 'text.secondary',
            backgroundColor: 'grey.50',
          },
        }}
      >
        {text} {badge}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexGrow: { xs: 0, md: 1 },
        gap: 1,
      }}
    >
      {renderMainButton()}

      <IconButton
        onClick={onReanalyze}
        disabled={isProcessing}
        sx={{
          display: 'flex',
          flexShrink: 0,
          p: '5px',
          my: 1,
          border: 1,
          borderColor: 'text.secondary',
          borderRadius: '8px',
        }}
      >
        <Replay fontSize="small" sx={{ color: 'text.secondary' }} />
      </IconButton>

      {hasResults && (
        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleViewObjects}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Objects</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleReanalyzeFromMenu}>
            <ListItemIcon>
              <Replay fontSize="small" />
            </ListItemIcon>
            <ListItemText>Re-analyze Photos</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
};
